/**
 * Cloudflare Access identity bridge.
 *
 * When the app is served behind Cloudflare Access at
 * app.berserkr.artemations.com, Cloudflare attaches a CF_Authorization
 * cookie containing a signed JWT. We don't try to verify that JWT
 * client-side — instead we call Cloudflare's identity endpoint, which
 * is served on every Access-protected domain at:
 *
 *     /cdn-cgi/access/get-identity
 *
 * That endpoint:
 *   - Reads the CF_Authorization cookie
 *   - Validates it against Cloudflare's signing keys at the edge
 *   - Returns the user's identity claims as JSON, or 401 if missing/expired
 *
 * The security boundary is "did Cloudflare's edge accept the request?"
 * The client is a downstream consumer of the validated result, not a
 * second auth gate.
 *
 * Local-dev fallback: when running on localhost (no Cloudflare Access in
 * the request path), the user can supply a fake identity via
 *   ?dev_email=their@email           (per-page-load override)
 *   VITE_DEV_DEFAULT_EMAIL=...        (default in .env.local)
 * Dev identities are clearly marked in `amr` so prod code can detect
 * them if it ever cares.
 */

export interface CfAccessIdentity {
  email: string;
  name?: string;
  /** Stable user identifier — Cloudflare user UUID, or a dev pseudo-id. */
  sub: string;
  /** Authentication methods — e.g. ["pwd"], ["mfa"]; ["dev_shim"] for local. */
  amr: string[];
  /** True if this identity came from the dev shim, not Cloudflare Access. */
  isDev: boolean;
}

const IDENTITY_ENDPOINT = "/cdn-cgi/access/get-identity";

async function fetchCfAccessIdentity(): Promise<CfAccessIdentity | null> {
  try {
    const res = await fetch(IDENTITY_ENDPOINT, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return null;

    // Cloudflare returns JSON like:
    //   { id, name, email, amr: [...], iat, ... }
    const data = (await res.json()) as Record<string, unknown>;
    const email = typeof data.email === "string" ? data.email : null;
    if (!email) return null;

    const id =
      typeof data.id === "string"
        ? data.id
        : typeof data.sub === "string"
          ? data.sub
          : `cf-${email}`;

    const amrRaw = Array.isArray(data.amr) ? data.amr : [];
    const amr = amrRaw.filter((x): x is string => typeof x === "string");

    return {
      email: email.toLowerCase(),
      name: typeof data.name === "string" ? data.name : undefined,
      sub: id,
      amr: amr.length > 0 ? amr : ["pwd"],
      isDev: false,
    };
  } catch {
    return null;
  }
}

function readDevShimIdentity(): CfAccessIdentity | null {
  if (typeof window === "undefined") return null;

  // Hard gate: the dev shim is ONLY available in Vite dev mode.
  // Production builds (`npm run build`) set import.meta.env.DEV=false,
  // which makes this short-circuit before reading the URL param or
  // env var — so a `?dev_email=` query string in production cannot
  // bypass Cloudflare Access.
  if (!import.meta.env.DEV) return null;

  const params = new URLSearchParams(window.location.search);
  const urlEmail = params.get("dev_email");
  const envEmail = import.meta.env.VITE_DEV_DEFAULT_EMAIL;
  const email = urlEmail ?? envEmail;

  if (!email) return null;

  const localPart = email.split("@")[0] ?? "user";
  return {
    email: email.toLowerCase(),
    name: localPart,
    sub: `dev-${email}`,
    amr: ["dev_shim"],
    isDev: true,
  };
}

/**
 * Resolve the current authenticated identity, or null if unauthenticated.
 *
 * Resolution order:
 *   1. Real Cloudflare Access identity (production path).
 *   2. Dev shim (?dev_email or VITE_DEV_DEFAULT_EMAIL) — only used as
 *      fallback when CF Access returns nothing, so production accidentally
 *      hitting these doesn't silently downgrade auth.
 */
export async function getCurrentIdentity(): Promise<CfAccessIdentity | null> {
  const cf = await fetchCfAccessIdentity();
  if (cf) return cf;
  return readDevShimIdentity();
}

/** Whether the dev shim is currently active. Useful for badge-in-UI. */
export function devShimAvailable(): boolean {
  return readDevShimIdentity() !== null;
}
